import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useMeetingsV2 } from "../../../context/MeetingV2Context";
import toast from "react-hot-toast";
import { getAllStudentCourses } from "../../../services/course.service";
import { getStudentAssignmentStats } from "../../../services/assignment.service";
import calculateAttendance from "../../../utils/Functions/CalculateStudentAttendencePercentage";
import AssignmentStatusChart from "./AssignmentStatusChart";

import { DashboardSkeleton } from "./ui/Skeleton";
import HeroStatsBar from "./ui/HeroStatsBar";
import SectionHeader from "./ui/SectionHeader";
import CourseCard from "./ui/CourseCard";
import LiveClassStrip from "./ui/LiveClassStrip";
import AssignmentTable from "./ui/AssignmentTable";

const DashboardSemesterContent = ({ setActiveSection, semNumber, onCourseClick }) => {
  const navigate = useNavigate();
  const { getMeetingsForCourse, fetchMeetingsForCourse } = useMeetingsV2();

  const [coursesData, setCoursesData] = useState({ courses: [], user: {} });
  const [allCoursesData, setAllCoursesData] = useState({ courses: [], user: {} });
  const [assignmentStats, setAssignmentStats] = useState({
    allAssignments: 0,
    submitted: 0,
    pending: 0,
    returned: 0,
    courses: [],
  });
  const [loading, setLoading] = useState(true);
  const [assignmentStatsLoading, setAssignmentStatsLoading] = useState(true);

  const allAssignmentsCount = assignmentStats.allAssignments || 0;
  const pendingAssignmentsCount = assignmentStats.pending || 0;
  const submittedAssignmentsCount = assignmentStats.submitted || 0;
  const returnedAssignmentsCount = assignmentStats.returned || 0;

  const getAllAssignments = () => {
    if (!assignmentStats.courses || assignmentStats.courses.length === 0) return [];
    const assignments = [];
    assignmentStats.courses.forEach((course) => {
      if (course.latestAssignment) {
        assignments.push({
          ...course.latestAssignment,
          courseName: course.courseTitle,
          courseId: course.courseId,
          courseCode: course.courseCode,
          courseImage: course.courseImage,
        });
      }
    });
    return assignments.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  };

  const getAssignmentStatus = (assignment) => {
    if (assignment.studentSubmission?.status === "returned") return "returned";
    if (assignment.hasSubmitted === true) return "submitted";
    return "not_started";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const data = await getAllStudentCourses();
        setAllCoursesData(data);
      } catch (err) {
        console.log(err);
        toast.error("Failed to load courses. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  useEffect(() => {
    const fetchAssignmentStats = async () => {
      try {
        setAssignmentStatsLoading(true);
        const data = await getStudentAssignmentStats();
        setAssignmentStats({
          allAssignments: data.allAssignments || 0,
          submitted: data.submitted || 0,
          pending: data.pending || 0,
          returned: data.returned || 0,
          courses: data.courses || [],
        });
      } catch (err) {
        console.error("Error fetching assignment stats:", err);
        toast.error("Failed to load assignment statistics. Please try again later.");
      } finally {
        setAssignmentStatsLoading(false);
      }
    };
    fetchAssignmentStats();
  }, []);

  useEffect(() => {
    if (!allCoursesData.courses || allCoursesData.courses.length === 0) return;
    if (semNumber != null) {
      const filteredCourses = allCoursesData.courses.filter((course) => {
        const courseSemNumber = course.semNumber || course.semester?.semNumber;
        return courseSemNumber === semNumber;
      });
      setCoursesData({ ...allCoursesData, courses: filteredCourses });
    } else {
      setCoursesData(allCoursesData);
    }
  }, [semNumber, allCoursesData]);

  useEffect(() => {
    if (coursesData.courses && coursesData.courses.length > 0) {
      const uniqueCourseIds = [...new Set(coursesData.courses.map((c) => c._id).filter(Boolean))];
      uniqueCourseIds.forEach((courseId) => {
        fetchMeetingsForCourse(courseId).catch(() => {});
      });
    }
  }, [coursesData.courses, fetchMeetingsForCourse]);

  const getThisWeekMeetings = useMemo(() => {
    if (!coursesData.courses || coursesData.courses.length === 0) return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);
    const allMeetings = [];
    coursesData.courses.forEach((course) => {
      if (!course?._id) return;
      const courseMeetings = getMeetingsForCourse(course._id) || [];
      courseMeetings.forEach((meeting) => {
        const start = meeting.instanceStart || meeting.start;
        if (!start) return;
        const startDate = new Date(start);
        if (startDate >= today && startDate <= endOfWeek) {
          allMeetings.push(meeting);
        }
      });
    });
    return allMeetings;
  }, [coursesData.courses, getMeetingsForCourse]);

  const upcomingEvents = useMemo(() => {
    return getThisWeekMeetings
      .filter((m) => m.status === "upcoming" || m.status === "live")
      .sort((a, b) => new Date(a.instanceStart || a.start) - new Date(b.instanceStart || b.start))
      .slice(0, 5);
  }, [getThisWeekMeetings]);

  const getStatusStyle = (status) => {
    const styles = {
      submitted: "text-emerald-600 bg-emerald-50 border-emerald-200 dark:text-emerald-400 dark:bg-emerald-900/30 dark:border-emerald-700",
      returned: "text-orange-600 bg-orange-50 border-orange-200 dark:text-orange-400 dark:bg-orange-900/30 dark:border-orange-700",
      in_progress: "text-blue-600 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-900/30 dark:border-blue-700",
      not_started: "text-gray-600 bg-gray-50 border-gray-200 dark:text-gray-300 dark:bg-gray-700/50 dark:border-gray-600",
    };
    return styles[status] || styles.not_started;
  };

  const getStatusLabel = (status) => {
    const labels = {
      submitted: "Submitted",
      returned: "Returned",
      in_progress: "In Progress",
      not_started: "Not Started",
    };
    return labels[status] || "Not Started";
  };

  const handleAssignmentClick = (assignment) => {
    if (onCourseClick) {
      onCourseClick(assignment.courseId, { initialTab: "Graded", assignmentId: assignment._id });
    } else {
      navigate(`/student/course/${assignment.courseId}?section=Graded&assignment=${assignment._id}`);
    }
  };

  if (loading || assignmentStatsLoading) {
    return <DashboardSkeleton />;
  }

  const recentAssignments = getAllAssignments().slice(0, 4);

  const attendanceValue = (() => {
    try {
      const result = calculateAttendance(coursesData.user._id, coursesData.courses);
      return `${Math.round(result.overall.attendancePercentage) || "-"}%`;
    } catch {
      return "-%";
    }
  })();

  const heroStats = [
    {
      id: "Courseware",
      value: coursesData?.user?.totalCourses || 0,
      label: "Active Courses",
    },
    {
      id: "Assignment",
      value: pendingAssignmentsCount,
      label: "Pending Tasks",
    },
    {
      id: "LiveClass",
      value: getThisWeekMeetings.length,
      label: "This Week",
    },
    {
      id: "MyStats",
      value: attendanceValue,
      label: "Attendance",
    },
  ];

  return (
    <div className="space-y-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Hero Stats Bar */}
      <HeroStatsBar stats={heroStats} onStatClick={setActiveSection} />

      {/* Courses + Assignment Chart — 7:5 grid */}
      <div className="px-2 sm:px-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Recent Courses */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="lg:col-span-7"
          >
            <SectionHeader
              title="Recent Courses"
              action="View All"
              onAction={() => setActiveSection("Courseware")}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {coursesData.courses?.slice(0, 4).map((course, index) => (
                <CourseCard key={course._id || index} course={course} index={index} onCourseClick={onCourseClick} />
              ))}
            </div>
          </motion.div>

          {/* Assignment Status Chart */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="lg:col-span-5"
          >
            <SectionHeader title="Assignment Status" />
            <div className="bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-sm dark:shadow-none p-4 sm:p-6">
              <AssignmentStatusChart
                allAssignmentsCount={allAssignmentsCount}
                pendingAssignmentsCount={pendingAssignmentsCount}
                submittedAssignmentsCount={submittedAssignmentsCount}
                returnedAssignmentsCount={returnedAssignmentsCount}
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Live This Week */}
      <div className="px-2 sm:px-4">
        <SectionHeader
          title="Live This Week"
          action="View All"
          onAction={() => setActiveSection("LiveClass")}
        />
        <LiveClassStrip events={upcomingEvents} />
      </div>

      {/* Recent Assignments */}
      <div className="px-2 sm:px-4 pb-8">
        <SectionHeader title="Recent Assignments" />
        <AssignmentTable
          assignments={recentAssignments}
          onAssignmentClick={handleAssignmentClick}
          getAssignmentStatus={getAssignmentStatus}
          getStatusStyle={getStatusStyle}
          getStatusLabel={getStatusLabel}
          formatDate={formatDate}
        />
      </div>
    </div>
  );
};

export default DashboardSemesterContent;
